"use client"

import React, {ReactNode, useEffect} from "react";
import {ProductType} from "@/types/Product";
import Link from "next/link";
import {ArrowTrendingDownIcon, BanknotesIcon, CalendarDaysIcon} from "@heroicons/react/24/outline";
import jMoment from "moment-jalaali";
import PersianNumber from "@/components/utils/PersianNumber";
import {ChevronLeftIcon} from "@heroicons/react/16/solid";
import ShareLikeBox from "@/components/utils/ShareLikeBox";
import {PackageType} from "@/types/Package";
import FormatPrice from "@/components/utils/FormatPrice";
import {useAppSelector} from "@/redux/hooks";

interface props {
    item: Partial<ProductType> | Partial<PackageType>
    link: string,
    detail?: ReactNode,
    onFavoriteChanged?: () => void;
    isProduct?: boolean;
    isPackage?: boolean;
    keyId?: number;
}

const LandingProductCard: React.FC<props> = ({
                                                 item,
                                                 link,
                                                 keyId,
                                                 onFavoriteChanged,
                                                 detail,
                                                 isProduct = false,
                                                 isPackage = false
                                             }) => {

    const product = item as Partial<ProductType>;

    let statusConstant = [];
    if (isProduct) {
        // @ts-ignore
        statusConstant = useAppSelector((state) => state.constants.data?.product?.Product?.status);
    }
    if (isPackage) {
        // @ts-ignore
        statusConstant = useAppSelector((state) => state.constants.data?.product?.Package?.status);
    }

    const findStatus = (value: any) => {
        if (statusConstant) {
            const result = statusConstant?.find((item: any[]) => item[0] === value);
            return result ? result[1] : "";
        }
    };

    const status = item.status;

    let button;

    if (status == 1) {
        button = (
            <button
                className="bg-customYellow hover:bg-warning/95 text-black w-full font-bold text-sm py-3 pt-2.5 rounded-sm">
                همین حالا ثبت‌نام کن!
            </button>
        );
    } else if (status == 6) {
        button = (
            <button
                className="bg-customRed hover:bg-danger/95 text-white w-full font-bold text-sm py-3 pt-2.5 rounded-sm">
                ظرفیت تکمیل شد!
            </button>
        );
    } else {
        button = (
            <button
                className="bg-gray-500 hover:bg-gray-500/95 text-white w-full font-bold text-sm py-3 pt-2.5 rounded-sm">
                {status && findStatus(status) ? findStatus(status) : "غیرفعال"}
            </button>
        );
    }

    return (
        <Link href={`${link}${item.id}`}
              className="relative group bg-white border border-gray-300 flex flex-col" key={keyId}>

            {/*Title*/}

            <div
                className="items-center text-sm font-bold pb-3 pt-4 pr-[20px] line-clamp-2 leading-12 min-h-[75px] space-y-2">
                {/*{isProduct && (*/}
                <p className="font-light text-sm block">{item.subtitle}</p>
                {/*)}*/}
                <p className="font-bold text-sm block">{item.name}</p>
            </div>

            <hr className="h-px bg-gray-300 text-gray-400 border-0"/>

            {/*Date*/}

            <div className="flex px-5 w-full items-center text-xs text-gray-500">
                {isProduct && (
                    <div className="flex w-full" key={`date-${keyId}`}>
                        {item.start_at ? <CalendarDay date={item.start_at}/> : <div className="w-1/2"></div>}
                        <hr className="mx-4 h-10 w-px bg-gray-300"/>
                        {item.end_at ? <CalendarDay date={item.end_at}/> : <div className="w-1/2"></div>}
                    </div>
                )}
                {isPackage && (
                    <div className="flex w-full" key={`price-${keyId}`}>
                        <div className="inline-flex items-center w-1/2">
                            <BanknotesIcon className="size-5"/>
                            <div className="inline-flex items-center text-gray-600 pt-0.5">
                                <span className="mx-0.5">قیمت اولیه:</span>
                                <p className="font-bold">
                                    <FormatPrice price={item.price}/>
                                </p>
                            </div>
                        </div>
                        <hr className="mx-4 h-10 w-px bg-gray-300"/>
                        <div className="inline-flex items-center w-1/2">
                            <ArrowTrendingDownIcon className="size-5"/>
                            <div className="inline-flex items-center text-gray-600 pt-0.5">
                                <span className="mx-0.5">قیمت نهایی:</span>
                                <p className="font-bold">
                                    {item.discounted_price ? (
                                        <FormatPrice price={item.discounted_price}/>
                                    ) : (
                                        "-"
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <hr className="h-px bg-gray-200 text-gray-400 border-0"/>

            {/*Event type*/}
            <div
                className="flex justify-between items-center pr-5 pl-2  text-xs py-4" key={`age-${keyId}`}>
                <div>
                    {isProduct && (
                        item.age_range_from && item.age_range_to && (
                            <div className="p-1 bg-gray-200 rounded-md">
                                <p className="space-x-1">
                                    <span>رده سنی </span>
                                    <PersianNumber number={item.age_range_from}/>
                                    <span>تا</span>
                                    <PersianNumber number={item.age_range_to}/>
                                    <span>سال</span>
                                </p>
                            </div>
                        )
                    )}
                    {isPackage && (
                        detail
                    )}
                </div>
                <div
                    key={item.id}
                    // href={`${link}${item.id}`}
                    // prefetch={false}
                    className="relative group my-1 hover:cursor-pointer"
                >
                    <div className="inline-flex pt-0.5 text-primary hover:scale-105">
                        مشاهده بیشتر
                        <ChevronLeftIcon className="size-4"/>
                    </div>
                </div>
            </div>

            {/*Image*/}

            <div className="relative overflow-hidden aspect-[3/2]">
                <img
                    src={isPackage ? item.image : (item as ProductType).landing_page_image}
                    alt={item.name}
                    className="w-full h-full object-fit"
                />

                {/* نوشته ثابت در بالا سمت چپ */}
                {item?.allowed_national_ids && (
                    <div
                        className="absolute top-0 left-0 bg-danger text-white text-xs px-2 py-1">
                        فقط کاربران مجاز
                    </div>
                )}

                {/* نوشته ثابت در پایین سمت چپ */}
                {item.category?.name && (
                    <div
                        className="absolute bottom-0 left-0 bg-customColor text-white text-xs px-2 py-1">
                        {item.category.name}
                    </div>
                )}
            </div>


            {/*Description*/}

            <p className="text-justify px-5 mb-2 mt-4 text-sm line-clamp-3 sm:line-clamp-6 flex-grow">
                {item.description}
            </p>

            {/*Submit & Like & Share Buttons*/}

            <div className="flex items-center px-5 py-3 pb-5 rtl:space-x-reverse mt-auto gap-2">
                <div
                    // href={`${link}${item.id}`}
                    className="w-8/12"
                >
                    {button}
                </div>

                <div
                    className="w-4/12 flex border border-gray-500 rounded-sm">
                    <ShareLikeBox
                        product={`${link}${item.id}`}
                        onFavoriteChanged={onFavoriteChanged}
                        is_favorite={item.is_favorite}
                    />
                </div>
            </div>
        </Link>
    )
}

export default LandingProductCard;

export const CalendarDay = ({date}: { date: string }) => {
    return (
        <div className="inline-flex items-center w-1/2">
            <CalendarDaysIcon className="size-5"/>
            <div className="flex items-center pt-1.5">
                <p className="text-[14px] mr-2">
                    {(new Intl.DateTimeFormat('fa-IR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    }).format(jMoment(date, 'YYYY-MM-DDTHH:mm:ss').toDate()))}
                </p>
            </div>
        </div>
    );
}